// Общая страница дашборда: отображает типы упражнений для преподавателей и редиректит студентов на /student.

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import useAuthStore from '../store/authStore';
import useExerciseStore from '../store/exerciseStore';
import AppNavbar from '../components/common/AppNavbar';

/**
 * Кликабельная карточка навигации для дашборда администратора.
 */
function AdminNavCard({ icon, title, description, to, disabled }) {
  const navigate = useNavigate();
  return (
    <Card
      className="border-0 h-100"
      onClick={() => !disabled && to && navigate(to)}
      style={{
        borderRadius: '0.875rem',
        background: '#fff',
        boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.1)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.07)';
      }}
    >
      <Card.Body className="p-4">
        <div style={{ fontSize: '1.75rem', marginBottom: '0.75rem', lineHeight: 1 }}>{icon}</div>
        <h6 className="fw-bold mb-1" style={{ color: '#1e2a4a', fontSize: '0.9375rem' }}>{title}</h6>
        <p className="text-muted mb-0" style={{ fontSize: '0.8125rem', lineHeight: 1.55 }}>{description}</p>
      </Card.Body>
    </Card>
  );
}

/** Группа карточек с заголовком секции */
function AdminCardGroup({ heading, items }) {
  return (
    <div className="mb-4">
      <h6 className="fw-bold text-muted mb-3" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {heading}
      </h6>
      <Row className="g-3">
        {items.map((item) => (
          <Col xs={12} sm={6} md={4} key={item.title}>
            <AdminNavCard {...item} />
          </Col>
        ))}
      </Row>
    </div>
  );
}

/** Возвращает вариант Badge по уровню сложности типа */
function levelBadgeVariant(level) {
  if (level <= 1) return 'success';
  if (level <= 2) return 'info';
  if (level <= 3) return 'primary';
  if (level <= 4) return 'warning';
  return 'danger';
}

export default function DashboardPage() {
  const { user, fetchMe } = useAuthStore();
  const { exercises, types, fetchExercises, fetchTypes, setCurrentExercise } =
    useExerciseStore();
  const navigate = useNavigate();

  // Редирект чистых студентов в студенческий раздел
  useEffect(() => {
    if (!user) return;
    const roles = user.roles ?? [];
    const isOnlyStudent = roles.length > 0 && roles.every((r) => r === 'student');
    if (isOnlyStudent) {
      navigate('/student', { replace: true });
    }
  }, [user]);

  useEffect(() => {
    Promise.all([fetchMe(), fetchExercises(), fetchTypes?.()]);
  }, []);

  const typeMap = new Map((types ?? []).map((t) => [t.id, t]));
  const isAdmin = user?.roles?.includes('admin');

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Доброе утро';
    if (hour < 18) return 'Добрый день';
    return 'Добрый вечер';
  };

  // ── Дашборд администратора ────────────────────────────────────────────────
  if (isAdmin) {
    return (
      <>
        <AppNavbar />
        <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
          <Container className="py-4">
            {/* Приветствие */}
            <div className="mb-4 pb-3" style={{ borderBottom: '1px solid #e9ecef' }}>
              <h5 className="fw-bold mb-0" style={{ letterSpacing: '-0.01em', color: '#1e2a4a' }}>
                {greeting()}, {user?.name ?? '...'}
              </h5>
              <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.875rem' }}>
                Панель управления
              </p>
            </div>

            {/* Группа 1 — Материалы */}
            <AdminCardGroup
              heading="Материалы"
              items={[
                {
                  icon: '🗂',
                  title: 'Типы упражнений',
                  description: 'Создавайте и редактируйте типы упражнений по разделам',
                  to: '/teacher/exercise-types',
                },
                {
                  icon: '📝',
                  title: 'Упражнения',
                  description: 'Управляйте упражнениями и вариантами ответов',
                  to: '/teacher/exercises',
                },
                {
                  icon: '📊',
                  title: 'Уровни сложности',
                  description: 'Настройте уровни сложности для упражнений',
                  to: '/admin/complexity-levels',
                },
              ]}
            />

            {/* Группа 2 — Студенты */}
            <AdminCardGroup
              heading="Студенты"
              items={[
                {
                  icon: '🎓',
                  title: 'Студенты',
                  description: 'Просматривайте список студентов и их прогресс',
                  to: '/admin/students',
                },
                {
                  icon: '📈',
                  title: 'Статистика',
                  description: 'Общая статистика по выполнению упражнений',
                  to: '/admin/students',
                  disabled: false,
                },
              ]}
            />

            {/* Группа 3 — Пользователи */}
            <AdminCardGroup
              heading="Пользователи"
              items={[
                {
                  icon: '👥',
                  title: 'Пользователи',
                  description: 'Управляйте учётными записями и ролями пользователей',
                  to: '/admin/users',
                },
              ]}
            />
          </Container>
        </div>
      </>
    );
  }

  // ── Дашборд преподавателя ─────────────────────────────────────────────────
  return (
    <>
      <AppNavbar />

      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
        <Container className="py-4">
          {/* ── Приветствие ── */}
          <div className="mb-4 pb-3" style={{ borderBottom: '1px solid #e9ecef' }}>
            <h5 className="fw-bold mb-0" style={{ letterSpacing: '-0.01em', color: '#1e2a4a' }}>
              {greeting()}, {user?.name ?? '...'}
            </h5>
            <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.875rem' }}>
              Выберите упражнение для практики
            </p>
          </div>

          {/* ── Статистика ── */}
          <Row className="g-3 mb-4">
            {[
              {
                label: 'Доступно упражнений',
                value: exercises.length,
                icon: '📝',
                color: 'var(--bs-primary)',
                bg: 'rgba(79,110,247,0.07)',
              },
              {
                label: 'Типов упражнений',
                value: (types ?? []).length,
                icon: '🗂',
                color: '#7c3aed',
                bg: 'rgba(124,58,237,0.07)',
              },
              {
                label: 'Ваша роль',
                value: user?.roles?.join(', ') || 'student',
                icon: '👤',
                color: '#28a745',
                bg: 'rgba(40,167,69,0.07)',
              },
            ].map((stat) => (
              <Col xs={12} sm={4} key={stat.label}>
                <Card className="border-0 h-100" style={{ borderRadius: '0.875rem', background: '#fff', boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}>
                  <Card.Body className="d-flex align-items-center gap-3 p-3">
                    <div
                      style={{
                        width: '2.75rem',
                        height: '2.75rem',
                        borderRadius: '0.75rem',
                        background: stat.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                        flexShrink: 0,
                      }}
                    >
                      {stat.icon}
                    </div>
                    <div>
                      <div className="fw-bold" style={{ color: stat.color, lineHeight: 1.2 }}>
                        {stat.value}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.1rem' }}>
                        {stat.label}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* ── Список упражнений ── */}
          <div>
            <h6 className="fw-semibold mb-3" style={{ color: '#1e2a4a', letterSpacing: '0.01em' }}>
              Упражнения
              {exercises.length > 0 && (
                <Badge bg="light" text="dark" className="ms-2 fw-normal" style={{ fontSize: '0.75rem' }}>
                  {exercises.length}
                </Badge>
              )}
            </h6>

            {exercises.length === 0 ? (
              <Card className="border-0 text-center py-5" style={{ background: '#fff', borderRadius: '0.875rem', boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}>
                <Card.Body>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
                  <h6 className="fw-semibold text-muted mb-1">Упражнений пока нет</h6>
                  <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                    Преподаватель ещё не добавил упражнения
                  </p>
                </Card.Body>
              </Card>
            ) : (
              <Row className="g-3">
                {exercises.map((ex) => {
                  const type = typeMap.get(ex.type_id);
                  return (
                    <Col xs={12} md={6} xl={4} key={ex.id}>
                      <Card
                        className="border-0 ewl-exercise-card h-100"
                        onClick={() => setCurrentExercise(ex)}
                        style={{
                          background: '#fff',
                          borderRadius: '0.875rem',
                          boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                        }}
                      >
                        <Card.Body className="p-3">
                          <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                            <h6
                              className="fw-semibold mb-0"
                              style={{ color: '#1e2a4a', fontSize: '0.9375rem', lineHeight: 1.4 }}
                            >
                              {ex.name}
                            </h6>
                            {type && (
                              <Badge
                                bg={levelBadgeVariant(type.hard_level)}
                                style={{ flexShrink: 0, fontSize: '0.7rem' }}
                              >
                                {type.hard_level} ур.
                              </Badge>
                            )}
                          </div>

                          {type && (
                            <div className="mb-2">
                              <span
                                style={{
                                  fontSize: '0.75rem',
                                  color: '#6c757d',
                                  background: '#f4f6fb',
                                  borderRadius: '0.35rem',
                                  padding: '0.15rem 0.5rem',
                                }}
                              >
                                {type.name}
                              </span>
                            </div>
                          )}

                          {ex.task && (
                            <p
                              className="text-muted mb-0"
                              style={{ fontSize: '0.8125rem', lineHeight: 1.5 }}
                            >
                              {ex.task.length > 90 ? ex.task.slice(0, 90) + '…' : ex.task}
                            </p>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </div>
        </Container>
      </div>
    </>
  );
}
